import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { listMediaAssets, uploadMediaAsset } from '@/services/media';
import type { MediaAsset } from '@/domain/types';

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif']);

function isPickable(a: MediaAsset): boolean {
  return (
    (!!a.mimeType?.startsWith('image/') || IMAGE_EXTS.has(a.fileExtension?.toLowerCase() ?? '')) &&
    a.visibility === 'public' &&
    !!a.publicUrl
  );
}

function fmtSize(bytes: number | null) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type Tab = 'library' | 'upload';

interface Props {
  selected?: string;
  onSelect: (url: string) => void;
  onClose: () => void;
  title?: string;
}

export function MediaPicker({ selected, onSelect, onClose, title = 'Choose Image' }: Props) {
  const [tab, setTab]         = useState<Tab>('library');
  const [assets, setAssets]   = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [hovered, setHovered] = useState('');
  const [picked, setPicked]   = useState(selected ?? '');

  // Upload state
  const [isDragging, setIsDragging]     = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [uploadError, setUploadError]   = useState('');
  const [uploadedFile, setUploadedFile] = useState<{ name: string; preview: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter  = useRef(0);

  const loadAssets = () =>
    listMediaAssets()
      .then((all) => setAssets(all.filter(isPickable)))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { loadAssets(); }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const filtered = assets.filter((a) =>
    !search || a.displayName.toLowerCase().includes(search.toLowerCase()),
  );

  const handleConfirm = () => {
    if (picked) { onSelect(picked); onClose(); }
  };

  // ── Upload handlers ──────────────────────────────────────────────────────────

  const processFile = async (file: File) => {
    if (!file.type.startsWith('image/') && !IMAGE_EXTS.has(file.name.split('.').pop()?.toLowerCase() ?? '')) {
      setUploadError('Please upload an image file (JPG, PNG, WebP, GIF, SVG).');
      return;
    }
    setUploadError('');
    setUploading(true);
    setUploadedFile({ name: file.name, preview: URL.createObjectURL(file) });
    try {
      const asset = await uploadMediaAsset(file, 'public-media');
      await loadAssets();
      if (asset.publicUrl) {
        setPicked(asset.publicUrl);
        setTab('library');
      }
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden" style={{ maxHeight: '88vh' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
                <i className="ri-image-line text-brand-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-surface-900">{title}</h2>
                <p className="text-xs text-surface-400">{assets.length} image{assets.length !== 1 ? 's' : ''} in library</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors">
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-surface-200 shrink-0 px-6">
            {([
              ['library', 'ri-image-2-line', 'Media Library'],
              ['upload',  'ri-upload-cloud-2-line', 'Upload New'],
            ] as [Tab, string, string][]).map(([key, icon, label]) => (
              <button
                key={key}
                onClick={() => { setTab(key); setUploadError(''); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  tab === key
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-surface-500 hover:text-surface-700'
                }`}
              >
                <i className={icon} />
                {label}
              </button>
            ))}
          </div>

          {/* ── Library tab ── */}
          {tab === 'library' && (
            <>
              <div className="px-6 py-3 border-b border-surface-100 shrink-0">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search images…"
                    autoFocus
                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-surface-50"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 min-h-0">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <i className="ri-loader-4-line animate-spin text-3xl text-surface-300" />
                    <p className="text-sm text-surface-400">Loading media…</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-surface-100 flex items-center justify-center">
                      <i className="ri-image-line text-2xl text-surface-400" />
                    </div>
                    <p className="text-sm font-medium text-surface-600">
                      {assets.length === 0 ? 'No images in library yet' : 'No images match your search'}
                    </p>
                    <button
                      onClick={() => setTab('upload')}
                      className="text-xs text-brand-600 hover:underline flex items-center gap-1"
                    >
                      <i className="ri-upload-cloud-2-line" /> Upload an image
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {filtered.map((asset) => {
                      const isSelected = picked === asset.publicUrl;
                      const isHov      = hovered === asset.id;
                      return (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => setPicked(asset.publicUrl!)}
                          onMouseEnter={() => setHovered(asset.id)}
                          onMouseLeave={() => setHovered('')}
                          className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-150 focus:outline-none ${
                            isSelected
                              ? 'border-brand-500 shadow-lg shadow-brand-200/50 scale-[1.02]'
                              : 'border-surface-200 hover:border-brand-300 hover:shadow-md'
                          }`}
                        >
                          <div className="aspect-square bg-surface-100">
                            <img src={asset.publicUrl!} alt={asset.displayName} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-brand-500 flex items-center justify-center shadow-sm">
                              <i className="ri-check-line text-white text-xs font-bold" />
                            </div>
                          )}
                          {(isHov || isSelected) && (
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pt-4 pb-1.5">
                              <p className="text-white text-[10px] font-medium truncate leading-tight">{asset.displayName}</p>
                              {asset.fileSize && <p className="text-white/60 text-[9px]">{fmtSize(asset.fileSize)}</p>}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Upload tab ── */}
          {tab === 'upload' && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 min-h-0 overflow-y-auto">
              {/* Drop zone */}
              <div
                onDragEnter={handleDragEnter}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                className={`w-full max-w-lg rounded-2xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center gap-5 py-14 px-8 text-center ${
                  isDragging
                    ? 'border-brand-400 bg-brand-50'
                    : uploading
                    ? 'border-surface-200 bg-surface-50 cursor-wait'
                    : 'border-surface-300 bg-surface-50/50 hover:border-brand-400 hover:bg-brand-50/30'
                }`}
              >
                {uploading ? (
                  <>
                    {uploadedFile?.preview && (
                      <img src={uploadedFile.preview} alt="preview" className="w-24 h-24 rounded-xl object-cover border border-surface-200 shadow-sm" />
                    )}
                    <div className="flex flex-col items-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-3xl text-brand-500" />
                      <p className="text-sm font-semibold text-surface-700">Uploading{uploadedFile ? ` "${uploadedFile.name}"` : ''}…</p>
                      <p className="text-xs text-surface-400">Adding to your media library</p>
                    </div>
                  </>
                ) : isDragging ? (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center animate-bounce">
                      <i className="ri-upload-cloud-2-line text-3xl text-brand-600" />
                    </div>
                    <p className="text-base font-semibold text-brand-600">Drop your image here</p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-white border border-surface-200 shadow-sm flex items-center justify-center">
                      <i className="ri-upload-cloud-2-line text-3xl text-surface-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-surface-700 mb-1">
                        Drag & drop an image here
                      </p>
                      <p className="text-xs text-surface-400 mb-4">or click to browse your files</p>
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        {['JPG', 'PNG', 'WebP', 'GIF', 'SVG', 'AVIF'].map((fmt) => (
                          <span key={fmt} className="px-2 py-0.5 rounded-md bg-white border border-surface-200 text-xs text-surface-500 font-medium">
                            {fmt}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="px-6 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shadow-sm"
                    >
                      <i className="ri-folder-open-line mr-2" />Browse Files
                    </button>
                  </>
                )}
              </div>

              {uploadError && (
                <div className="mt-4 w-full max-w-lg flex items-center gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  <i className="ri-error-warning-line shrink-0" /> {uploadError}
                </div>
              )}

              <p className="mt-5 text-xs text-surface-400 text-center">
                Uploaded images are saved to your public media library and<br />automatically selected after upload.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-surface-200 flex items-center justify-between shrink-0 bg-surface-50/50">
            <div className="flex items-center gap-3 min-w-0">
              {picked ? (
                <>
                  <img src={picked} alt="selected" className="w-10 h-10 rounded-lg object-cover border border-surface-200 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-surface-700">Selected</p>
                    <p className="text-[11px] text-surface-400 truncate max-w-[220px]">{picked}</p>
                  </div>
                </>
              ) : (
                <p className="text-xs text-surface-400">
                  {tab === 'library' ? 'Click an image to select it' : 'Upload an image to select it'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!picked}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
              >
                Use This Image
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
